import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import SelectOTA from '~/features/base/components/forms/ota/SelectOTA';
import DeliverableTypeSelect from '~/features/base/components/forms/DeliverableTypeSelect';
import { CreateCampaignFormValues } from '~/features/campaigns/models/CreateCampaignFormValues';
import { DeliverableType } from '~/features/deliverables/constants/DeliverableType';
import {
    useLazyFetchDeliverableIdsQuery,
    useLazyFetchDeliverableVersionsQuery
} from '~/api/deliverables/DeliverablesManagement.api';
import {
    DeliverableSearchCriteria,
    DeliverableSearchCriteriaDeliverableTypeEnum
} from '~/api/models/deliverable-search-criteria';
import { toShortSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import Notification from '@rio-cloud/rio-uikit/Notification';

interface CampaignTargetDeliverableFormProps {
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    disabled?: boolean;
    formValues: CreateCampaignFormValues;
}

export const deliverableIdsByDeliverableType: Record<string, string[]> = {
    [DeliverableType.DISTRO]: ['DISTRO'],
};

const CampaignTargetDeliverableForm: React.FC<CampaignTargetDeliverableFormProps> = ({
    control,
    setValue,
    disabled = false,
    formValues
}) => {
    const [fetchDeliverableIds] = useLazyFetchDeliverableIdsQuery();
    const [fetchDeliverableDeliverableVersions] = useLazyFetchDeliverableVersionsQuery();
    const [deliverableIdByType, setDeliverableIdByType] = useState<Record<string, string[]>>(deliverableIdsByDeliverableType);
    const [deliverableVersions, setDeliverableVersions] = useState<string[]>([]);
    const intl = useIntl();

    const [deliverableType, deliverableId, deliverableVersion] = useWatch({
        control,
        name: ['deliverableType', 'deliverableId', 'deliverableVersion']
    });

    useEffect(() => {

        const getVersionOptions = async() => {
            if (deliverableType !== '' && deliverableId !== '') {
                try {
                    const res = await fetchDeliverableDeliverableVersions({
                        deliverableType: deliverableType as DeliverableSearchCriteriaDeliverableTypeEnum,
                        deliverableId: deliverableId
                    }).unwrap();
                    setDeliverableVersions(res.content.map(v => toShortSemanticVersion(v)));
                } catch (err) {
                    console.error(`Error fetching deliverable versions for`, err);
                }
            }
        };
        getVersionOptions().catch((error) => Notification.error(error));
    }, [deliverableType, deliverableId]);

    useEffect(() => {
        if (deliverableType && deliverableType !== '') {
            handleDeliverableTypeSelect(deliverableType);
        }
    }, []);

    const handleDeliverableTypeSelect = async(type: string) => {
        if (deliverableIdByType[type]) {
            return;
        }
        try {
            const criteria: DeliverableSearchCriteria = { deliverableType: type as DeliverableSearchCriteriaDeliverableTypeEnum };
            const res = await fetchDeliverableIds(criteria).unwrap();
            setDeliverableIdByType(prev => ({
                ...prev,
                [type]: res.content
            }));
        } catch (err) {
            console.error(`Error fetching deliverables for ${type}`, err);
        }
    };

    const getDeliverableOptions = (type: string) => {
        return deliverableIdByType[type]?.map(d => ({
            value: d,
            label: d,
            id: d
        }));
    };

    const getVersionOptions = () => {
        return deliverableVersions.map(d => ({
            value: d,
            label: d,
            id: d
        }));
    };

    return (
        <div className="campaign-target-deliverable-form">
            <div className="margin-bottom-20">
                <p className='text-color-gray'>
                    <FormattedMessage id='intl-msg:campaign:targetDeliverable:description'/>
                </p>
            </div>

            <div className="margin-bottom-20">
                <Controller
                    control={control}
                    name="deliverableType"
/*
                    rules={{ required: 'Deliverable type is required' }}
*/
                    render={({ field, fieldState: { error } }) => (
                        <DeliverableTypeSelect
                            value={field.value}
                            onChange={async(optionSelected) => {
                                field.onChange(optionSelected);
                                await handleDeliverableTypeSelect(optionSelected);
                            }}
                            hasError={!!error}
                            deviceType={formValues?.deviceType}
                        />
                    )}
                />
            </div>

            <div className="margin-bottom-20">
                <Controller
                    control={control}
                    name="deliverableId"
/*
                    rules={{ required: 'Deliverable ID is required' }}
*/
                    render={({ field, fieldState: { error } }) => {
                        const options = getDeliverableOptions(deliverableType);
                        return (
                            <SelectOTA
                                title={intl.formatMessage({ id: 'intl-msg:deliverableId' })}
                                disabled={!deliverableType}
                                options={options}
                                value={[field?.value]}
                                onChange={(selectedOption) => {
                                    field.onChange(selectedOption?.value);
                                    // Reset version when ID changes
                                    setValue('deliverableVersion', '');
                                }}
                                placeholder={intl.formatMessage({ id: 'intl-msg:deliverableId:select' })}
                                hasError={!!error}
                                errorMessage={error?.message}
                            />
                        );
                    }}
                />
            </div>

            <div className="margin-bottom-20">
                <Controller
                    control={control}
                    name="deliverableVersion"
/*
                    rules={{ required: 'Deliverable version is required' }}
*/
                    render={({ field, fieldState: { error } }) => {
                        const options = getVersionOptions();

                        return (
                            <SelectOTA
                                title={intl.formatMessage({ id: 'intl-msg:reportsBillingDeliverableVersion' })}
                                disabled={!deliverableId}
                                options={options || []}
                                value={[field?.value]}
                                onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                placeholder={intl.formatMessage({ id: 'intl-msg:version:select' })}
                                hasError={!!error}
                                errorMessage={error?.message}
                                isDisabled={disabled || !deliverableId}
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default CampaignTargetDeliverableForm;
